// 제품 타입 정의
export interface Product {
  id: string;
  name: string;
  nameEn: string;
  subtitle?: string;
  volume: string; // 제품 용량
  volumeOptions?: string[]; // 다중 용량 옵션 추가
  category: 'disease' | 'growth' | 'stress';
  description: string;
  thumbnail: string;
  image: string;
  features: string[];
  usage: string;
  dosage: string;
  recommendation: string;
  // 상세페이지용 추가 필드
  mechanism?: string;
  beforeAfterImage?: string;
  usageDetail?: string[];
  caution?: string[];
}

// 카테고리 정보
export const categories = {
  disease: {
    id: 'disease',
    title: '병해 방제 및 작물 보호',
    description: '작물의 질병을 직접 치료하거나 외부 침입으로부터 보호막을 형성하는 제품군',
    banner: {
      headCopy: '병해 방제',
      subCopy: '작물의 질병을 예방하고 치료하는 전문 솔루션',
      image: 'https://public.readdy.ai/ai/img_res/5ab1e895b092f74a916c1f5c4ddfe19f.jpg'
    }
  },
  growth: {
    id: 'growth',
    title: '생육 촉진 및 영양 공급',
    description: '뿌리 발달을 돕고 필수 영양소를 공급하여 기초 체력을 다지는 제품군',
    banner: {
      headCopy: '생육 촉진',
      subCopy: '뿌리 발달과 영양 공급으로 건강한 성장 지원',
      image: 'https://i.imgur.com/Q1qEhJI.jpeg'
    }
  },
  stress: {
    id: 'stress',
    title: '환경 스트레스 및 기능성',
    description: '극한 기상 조건(폭염, 한파, 서리)을 극복하고 시각적 품질을 높이는 제품군',
    banner: {
      headCopy: '환경 스트레스',
      subCopy: '극한 기후 조건에서도 작물을 보호하는 기능성 제품',
      image: 'https://readdy.ai/api/search-image?query=dramatic%20contrast%20scene%20of%20golf%20course%20green%20in%20early%20cold%20morning%2C%20surrounding%20ground%20covered%20with%20white%20frost%20and%20ice%20crystals%2C%20but%20treated%20grass%20area%20maintains%20vibrant%20healthy%20green%20color%2C%20misty%20atmosphere%2C%20professional%20golf%20course%20photography%2C%20cool%20blue%20tones%20contrasting%20with%20warm%20green%2C%20natural%20lighting&width=300&height=400&seq=catbanner3v5&orientation=portrait'
    }
  }
} as const;

export type CategoryType = keyof typeof categories;

// 전체 제품 데이터 (단일 소스)
export const products: Product[] = [
  // ===== 카테고리 1: 병해 방제 및 작물 보호 =====
  {
    id: 'sak-hwang',
    name: '싹황',
    nameEn: 'Sak-hwang',
    subtitle: '발효유황',
    volume: '1L',
    category: 'disease',
    description: '한국 전통 발효공법을 통해 제조된 100% 흡수율의 고품질 발효유황 자재입니다. 습한 기후에서 발생하는 각종 잔디 곰팡이병 예방은 물론, 작물의 면역력을 높여 고온 스트레스 완화와 생육 활성(그린 업)에 탁월한 효과를 발휘하는 친환경 안전 물질입니다.',
    thumbnail: 'https://i.imgur.com/5hrZCc4.jpeg',
    image: 'https://readdy.ai/api/search-image?query=premium%20yellow%20sulfur%20fertilizer%20bottle%20on%20clean%20white%20background%20with%20green%20leaves%2C%20professional%20product%20photography%2C%20high%20quality%20agricultural%20solution%2C%20minimalist%20style%2C%20soft%20lighting&width=400&height=500&seq=prod1&orientation=portrait',
    features: [
      '한국 전통 발효공법으로 제조되어 작물 투입 성분을 빠르고 효과 발현이 매우 탁월합니다.',
      '침투성과 이행성이 뛰어나 잔디의 생리활성은 물론 면역력을 증가시키는 매우 안전한 물질입니다.',
      '완전 액상 제형으로 진균성 병해 포자에 완전히 침투하여 사멸시키는 강력한 방제 능력을 갖추고 있습니다.',
      '발효 과정에서 항균 대사물질이 생성되어 세균 및 곰팡이성 병해 예방에 탁월합니다.',
      '글루타치온 분자의 생성을 도와 열 스트레스로 인한 산화적 손상을 완화하고 고온 스트레스를 완화해 줍니다.',
      '타 자재와의 혼용이 가능하며 경제적입니다.'
    ],
    usage: '비가 자주 와서 곰팡이병이 이어 문제가 되어 고민하는 분께 추천!',
    dosage: '물 1000L에 2L 희석 후 엽면살포 (2주 간격)',
    recommendation: '비가 자주 오는 환경에서 곰팡이병 예방과 치료에 효과적입니다.',
    mechanism: '주요 성분: 발효유황(100%), 발효대사산물 효소(5%), 방선균 및 유효미생물(적량), 붕소(0.05%), 몰리브덴(0.0005%) 함유.\\n병해 방제 메커니즘: 라지패치, 달라스팟, 브라운패치, 노균병 등 각종 곰팡이병 예방 및 방제에 효과적이며, 그린의 조류 및 이끼 억제에도 관여합니다.\\n단백질 및 효소 생성: 황화 아미노산인 시스테인, 메티오닌 합성을 도와 단백질 형성과 비타민 효소 생성에 기여합니다.\\n생육 활성화: 잔디의 전 생육 기간에 걸쳐 건전한 생육을 지원하며 특히 잔디 그린 업(Green-up) 효과가 탁월합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20grass%20lawn%20treatment%2C%20left%20side%20showing%20diseased%20yellow%20patches%2C%20right%20side%20showing%20healthy%20green%20grass%2C%20split%20screen%20comparison%2C%20professional%20photography%2C%20clean%20white%20background&width=800&height=400&seq=beforeafter1&orientation=landscape',
    usageDetail: [
      '권장 사용량: ㎡당 2ml의 약량을 사용합니다.',
      '희석 비율: 물 1,000L당 싹황 2L를 희석(500배액)하여 사용합니다.',
      '살포 방법: ㎡당 100~200ml의 물량으로 엽면살포하며, 2주 간격을 권장합니다.',
      '적용 시기: 그린의 경우 3월부터 비료 및 농약에 500배 희석 후 살포하며, 라지패치 방제 시에도 동일한 배율로 살포합니다.'
    ],
    caution: [
      '음용을 절대 금지합니다.',
      '아침 이슬이 마른 직후나 일몰 전에 사용하시면 효과가 더욱 좋습니다.',
      '발효 대사물질이 침전될 수 있으므로 사용 전 충분히 흔들어 주십시오.',
      '강 알칼리성, 강 산성 제제, 마이신 및 동제 계통의 농약이나 비료와 혼용 시 소량 혼용 실험 후 사용하십시오.',
      '사용 후 남은 제품은 밀봉하여 보관하십시오.'
    ]
  },
  {
    id: 'gyu-hwang',
    name: '규-황',
    nameEn: 'Gyu-hwang',
    subtitle: 'Sulfer-SiO',
    volume: '1L',
    category: 'disease',
    description: '전통 미생물 발효 유황과 액상 규산을 결합하여 병해충 관리와 잔디 조직 강화 기능을 동시에 제공하는 고기능성 자재입니다. 침투력과 이행성이 뛰어나 성분을 100% 흡수하며, 잔디의 직립성을 향상시키고 고온기에도 안전하게 사용할 수 있는 것이 특징입니다.',
    thumbnail: 'https://i.imgur.com/M20LBbY.jpeg',
    image: 'https://readdy.ai/api/search-image?query=modern%20white%20and%20green%20fertilizer%20bottle%20with%20silicon%20sulfur%20formula%20on%20clean%20white%20background%2C%20professional%20agricultural%20product%20photo%2C%20minimalist%20design%2C%20premium%20quality%2C%20soft%20studio%20lighting&width=400&height=500&seq=prod2&orientation=portrait',
    features: [
      '전통 미생물 발효 공법을 적용하여 잔디 및 모든 작물이 투입 성분을 100% 흡수할 수 있도록 제조되었습니다.',
      '병해충 관리와 잔디 조직체 강화를 한 번의 처리로 동시에 해결할 수 있어 효율적입니다.',
      '침투력과 이행성이 매우 뛰어나 식물체 내로 성분이 빠르게 전달됩니다.',
      '여름철 고온기에 사용해도 무방할 정도로 안정성이 뛰어난 자재입니다.'
    ],
    usage: '작물이 물러져 쓰러지거나 병충해가 걱정되어 고민하는 분께 추천!',
    dosage: '그린 기준 물 600L에 3L 희석 후 100~200ml/m² 살포',
    recommendation: '작물 조직을 강화하고 병충해 저항성을 높여줍니다.',
    mechanism: '주요 성분: 규산(30%), 발효유황(30% 또는 5%), 무수규산(30%), K2O(25%), 유기물(5%), 해조류(2%), 목초액(5%), 글루코스(5%), 붕소(0.05%), 몰리브덴(0.0005%) 등을 함유하고 있습니다.\\n발효 유황의 작용: 잔디 및 작물의 곰팡이성 병원균이나 포자에 직접 작용하여 이를 사멸시키는 역할을 합니다.\\n규산의 방어 기제: 식물체 표면에 큐티클층을 형성하여 외부 병원균의 침투를 물리적으로 막아주고 잔디의 직립성을 도와 생육을 지원합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20plant%20treatment%20showing%20stronger%20stems%2C%20left%20side%20weak%20drooping%20plants%2C%20right%20side%20upright%20healthy%20plants%2C%20split%20screen%2C%20professional%20photography&width=800&height=400&seq=beforeafter2&orientation=landscape',
    usageDetail: [
      '그린: 물 600L에 본 제제 3L를 희석한 후, ㎡당 100~200ml 비율로 살포합니다 (약 3,000~6,000㎡ 면적분).',
      '페어웨이(F/W): 물 1,000L에 본 제제 5L를 희석한 후, ㎡당 100~200ml 비율로 살포합니다 (약 5,000~10,000㎡ 면적분).',
      '영양 공급: 부족한 영양분 보충을 위해 아미노산 함유 자재나 뿌리 발근 도움 기능성 자재와 혼용하여 사용할 수 있습니다.',
      '관수 안내: 사용 시 물량에 따라 추가 관수를 실시해 주십시오.'
    ],
    caution: [
      '음용을 절대 금지합니다.',
      '아침 이슬이 마른 직후나 일몰 전에 사용하시면 효과가 더욱 좋습니다.',
      '발효 대사물질이 침전될 수 있으므로 사용 전 충분히 흔들어 주십시오.',
      '강산성 및 강알kalri성 제료나 농약과의 혼용은 피해주십시오.',
      '사용 후 남은 제품은 밀봉하여 보관하십시오.'
    ]
  },
  {
    id: 'fomino-s',
    name: 'F아미노-S',
    nameEn: 'F Amino S',
    subtitle: '',
    volume: '10L',
    category: 'disease',
    description: '18종 동물성 아미노산 베이스에 발효 유황을 첨가한 기능성 제제로 페어웨이 전용 필름 증강 및 병 예방에 효과적입니다.',
    thumbnail: 'https://i.imgur.com/tvNKvfn.jpeg',
    image: 'https://readdy.ai/api/search-image?query=dark%20amber%20amino%20acid%20sulfur%20fertilizer%20bottle%20on%20clean%20white%20background%2C%20professional%20agricultural%20product%20photo%2C%20premium%20quality%2C%20minimalist%20design%2C%20warm%20studio%20lighting&width=400&height=500&seq=prod10&orientation=portrait',
    features: [
      '18종의 동물성 아미노산을 베이스로 하여 잔디의 활력을 증강시키는 페어웨이 전용 액상 자재입니다.',
      '미생물 공법으로 제조된 발효 유황이 포함되어 있어 잔디의 병 예방 및 내병원성 향상에 효과적입니다.',
      '비해(비료해)나 약해 걱정 없이 안정적으로 사용할 수 있는 친환경적 관주용 제품입니다.',
      '발효 유황의 작용으로 토양 내 염류 축적을 방지하고 이미 축적된 염류를 해소하여 토양 환경을 개량합니다.',
      '고온기, 저온기, 장마기 등 불량 환경에서 수세가 약해진 잔디의 빠른 생리활성 및 회복을 돕습니다.'
    ],
    usage: '장마나 고온기에 작물 수세가 약해져 고민하는 분께 추천!',
    dosage: '약량기준 2~4ml/m² (10L당 5,000~10,000m² 사용)',
    recommendation: '장마철과 고온기 작물 수세 회복에 탁월한 효과를 발휘합니다.',
    mechanism: '· 주요 성분: 아미노산(30%), 펄빅산(5%), 발효유황(5%), 수용성 붕소(0.05%), 수용성 몰리브덴(0.0005%) 함유.\\n· 필수 아미노산 공급: 글루탐산, 라이신, 발린 등 필수 아미노산을 함유하여 전 생육 기간에 걸쳐 활성에 기여합니다.\\n· 황화 아미노산 합성: 발효 유황을 통해 시스테인, 메티오닌 등의 합성을 유도하여 단백질 및 효소 생성을 촉진합니다.\\n· 뿌리 생육 특화: 아미노산, 발효유황, 유카 성분이 상호작용하여 지하부 뿌리 발육을 유도하는 기능을 수행합니다.\\n· 광합성 및 에너지 대사: 엽록소와 탄수화물 생성을 지원하여 광합성 능력을 증대시키고 스트레스 저항성을 높입니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20fairway%20grass%20treatment%2C%20left%20side%20thin%20weak%20grass%2C%20right%20side%20thick%20healthy%20green%20grass%2C%20split%20screen%20comparison%2C%20golf%20course%20turf&width=800&height=400&seq=beforeafter3&orientation=landscape',
    usageDetail: [
      '잔디 면적 1m²당 2~4ml를 사용합니다.',
      '제품 기준: 10L 한 통으로 약 5,000~10,000m² 면적에 시비가 가능합니다.',
      '적용 방식: 수세 회복 및 품질 향상을 위해 관주용으로 사용하십시오.'
    ],
    caution: [
      '페어웨이 전용 제품이므로 용도에 맞게 사용하십시오.',
      '권장 사용량을 준수하여 잔디의 생육 상태에 따라 적절히 조절하십시오.',
      '제품의 변질을 막기 위해 직사광선을 피해 서늘한 곳에 보관하십시오.'
    ]
  },
  {
    id: 'sil-i-cal',
    name: '실리칼',
    nameEn: 'Sili-Cal',
    subtitle: '',
    volume: '1L',
    category: 'disease',
    description: '전통 미생물 발효 공법을 통해 규산과 칼슘을 최적의 비율로 혼합한 속효성 액상 비료입니다. 엽록소 형성과 뿌리 활력 증가를 돕고, 발효 유황 성분이 병충해에 대한 잔디의 저항력을 획기적으로 높여 튼튼한 생육을 지원합니다.',
    thumbnail: 'https://i.imgur.com/VGzZRZH.jpeg',
    image: 'https://readdy.ai/api/search-image?query=white%20calcium%20silicate%20fertilizer%20bottle%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20premium%20agricultural%20solution%2C%20minimalist%20style%2C%20bright%20lighting&width=400&height=500&seq=prod11&orientation=portrait',
    features: [
      '전통 발효 방법으로 제조된 규산과 칼슘이 조화를 이룬 속효성 액상 규산질 칼슘 비료입니다.',
      '조기 엽록소의 정상적인 형성을 돕고 뿌리의 활력을 증가시켜 맹아 재생과 녹화 촉진에 기여합니다.',
      '발효 유황을 함유하여 병충해에 대한 잔디의 저항성을 높여주는 혁신적인 수용성 비료입니다.',
      '잔디의 근권을 개량하여 튼튼한 잎, 줄기, 뿌리의 조기 회복을 도모합니다.',
      '발효 대사산물인 효소, 방선균, 누룩균, 유산균을 품질 높은 수용성으로 함유하고 있습니다.',
      '사용량에 따라 뿌리 길이 및 잔디 밀도 등 생육 면에서 확연한 차이를 확인할 수 있습니다.'
    ],
    usage: '줄기가 약해 쓰러지거나 녹화 촉진이 필요한 분께 추천!',
    dosage: '1~2cc/m², 이른 아침이나 일몰 후 살포 시 효과 증가',
    recommendation: '줄기 강화와 녹화 촉진을 동시에 실현하는 속효성 제품입니다.',
    mechanism: '주요 성분: 칼슘(10%), 규산(20%), 아미노산(10%), 해조류(1%), 펄빅산(1%), 수용성 붕소(0.05%), 수용성 몰리브덴(0.0005%)이 함유되어 있습니다.\\n미생물 활성: 수용성 규산과 칼슘이 체내에 빠르게 흡수되어 식물 조직을 강화하고 스트레스 저항성을 높입니다.\\n생리활성 촉진: 아미노산과 해조류 추출물, 펄빅산 등이 상호작용하여 잔디의 생리 대사를 활성화합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20plant%20stem%20strength%2C%20left%20side%20weak%20bent%20stems%2C%20right%20side%20strong%20upright%20stems%2C%20split%20screen%2C%20agricultural%20photography&width=800&height=400&seq=beforeafter4&orientation=landscape',
    usageDetail: [
      '약장 1-2cc/m² 기준',
      '희석 물량: ㎡당 100~200ml의 물량에 맞춰 사용하십시오.',
      '처리 방식: 수세 회복 및 품질 향상을 위해 관주용으로 사용하십시오.'
    ],
    caution: [
      '강알kal리성 및 강산성 제제, 유황합제, 기계유제, 석회보르도액 등과의 혼용을 금지합니다.',
      '10L 포장 단위 제품입니다.',
      '피부 접촉 시 흐르는 물에 깨끗이 세척하고 절대 음용하지 마십시오.'
    ]
  },
  {
    id: 'cal-sulfer',
    name: '칼서퍼',
    nameEn: 'Cal-Sulfer',
    subtitle: '',
    volume: '1L',
    category: 'disease',
    description: '수용성 활성 칼슘과 고함량의 발효유황을 결합하여 토양 환경 개선과 잔디의 영양 흡수를 동시에 해결하는 고기능성 자재입니다. 고결화된 토양 구조를 개선하여 뿌리가 깊게 내릴 수 있는 환경을 조성하고, 고온기 잔디의 활력을 효과적으로 증강시켜 줍니다.',
    thumbnail: 'https://i.imgur.com/DMFi9te.jpeg',
    image: 'https://readdy.ai/api/search-image?query=calcium%20sulfur%20fertilizer%20bottle%20with%20orange%20label%20on%20clean%20white%20background%2C%20professional%20agricultural%20product%20photo%2C%20modern%20design%2C%20premium%20quality%2C%20soft%20lighting&width=400&height=500&seq=prod12&orientation=portrait',
    features: [
      '고함량의 발효유황과 칼슘 함유로 염류가 집적된 토양을 빠르게 개선하는 데 도움을 줍니다.',
      '염류집적 토양 개선을 통해 잔디의 양이온 흡수와 수분 침투 및 생육을 원활하게 돕습니다.',
      '딱딱하게 굳은 토양 구조를 입단 구조로 개선하여 뿌리가 깊게 내릴 수 있는 최적의 환경을 만듭니다.',
      '일반적인 짚섬(CaSO4)과 달리 발효유황과 칼슘이 100% 용해되어 잔디에 전량 흡수됩니다.',
      '수용성 활성 칼슘 성분이 잔디의 칼슘 결핍 현상을 빠르게 해소해 줍니다.',
      '토양의 pH와 상관없이 안정적으로 칼슘을 공급할 수 있어 효율적입니다.',
      '하절기 고온 스트레스 상황에서 잔디의 활력을 증가시키는 데 도움을 줍니다.'
    ],
    usage: '토양이 약화되거나 뿌리가 깊게 내리지 못해 고민하는 분께 추천!',
    dosage: '그린 3L/600L, F/W 5L/1000L 토양살포',
    recommendation: '토양 구조를 개선하고 뿌리 발달 환경을 최적화합니다.',
    mechanism: '주요 성분: 발효유황(30%), 글루코산칼슘(18%), 질산칼슘(15%), 유기물(5%), 수용성 붕소(0.05%), 수용성 몰리브덴(0.0005%) 등을 함유하고 있습니다.\\n토양 환경 개선: 발효유황이 토양 내 축적된 염류를 해소하고, 칼슘 성분이 토양 입자를 떼알 구조(입단 구조)로 변화시켜 통기성과 배수성을 높입니다.\\n즉각적인 영양 흡수: 100% 용해되는 액상 제형으로 제조되어, 흡수 속도가 느린 기존 고체 자재들에 비해 즉각적인 영양 공급 효과를 발휘합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20soil%20improvement%2C%20left%20side%20compacted%20dry%20soil%2C%20right%20side%20healthy%20dark%20rich%20soil%20with%20roots%2C%20split%20screen%2C%20agricultural%20photography&width=800&height=400&seq=beforeafter5&orientation=landscape',
    usageDetail: [
      '그린: 물 600L에 본 제제 3L를 혼용하여 ㎡당 100~200ml 비율로 살포합니다 (약 3,000~6,000㎡ 면적분).',
      '페어웨이(F/W): 물 1,000L에 본 제제 5L를 혼용하여 ㎡당 100~200ml 비율로 살포합니다 (약 5,000~10,000㎡ 면적분).',
      '추가 관리: 아미노산 함유 자재나 뿌리 발근 기능성 자재와 혼용하여 영양분을 보충할 수 있으며, 물량에 따라 추가 관수를 실시해 주십시오.'
    ],
    caution: [
      'pH가 낮거나 인산 계통의 비료 및 농약과의 혼용은 삼가해 주십시오.',
      '고온기 및 여름철 한낮 시비는 피해주시기 바랍니다.',
      '절대 음용하지 마십시오.'
    ]
  },

  // ===== 카테고리 2: 생육 촉진 및 영양 공급 =====
  {
    id: 'kelp-amino',
    name: '켈프-아미노 3000',
    nameEn: 'Kelp-Amino 3000',
    subtitle: '',
    volume: '1L',
    category: 'growth',
    description: '전통 미생물 발효 공법으로 이온화한 아미노산과 해조 추출물을 결합하여 잔디의 기초 체력을 강화하는 고기능성 액비입니다. 지상부의 밀도 증진과 지하부 뿌리 발육을 이상적으로 촉진하며, 환경 스트레스나 병해충 피해를 입은 잔디의 신속한 회복을 돕는 100% 영양 자재입니다.',
    thumbnail: 'https://i.imgur.com/Y1pIwEh.jpeg',
    image: 'https://readdy.ai/api/search-image?query=dark%20green%20kelp%20amino%20acid%20fertilizer%20bottle%20on%20clean%20white%20background%20with%20seaweed%20elements%2C%20professional%20product%20photography%2C%20premium%20agricultural%20solution%2C%20minimalist%20style%2C%20natural%20lighting&width=400&height=500&seq=prod3&orientation=portrait',
    features: [
      '화학적 N.P.K 성분 없이 잔디 자체의 생리 대사를 개선하여 근본적인 기초 체력을 증진시킵니다.',
      '지상부의 잔디 밀도 향상과 지하부 뿌리 발육을 동시에 촉진하는 이상적인 생육 밸런스를 제공합니다.',
      '고온, 저온 등 이상기후로 인한 환경 스트레스를 감소시키고 잔디의 엽록소 생성을 도와 품질을 개선합니다.',
      '토양 내 유용 미생물을 활성화하고 토양의 이·화학성을 개선하여 비료의 흡수율을 높여줍니다.',
      '엽면과 뿌리 양쪽으로 동시에 흡수되어 비료 효과가 빠르고 탁월합니다.',
      '답압이 심하거나 고염류 피해를 입은 그린, 충병 피해를 입은 잔디에 적용 시 빠른 회복력을 보입니다.'
    ],
    usage: '뿌리 활력이 떨어지고 잔디가 듬성듬성해 고민하는 분께 추천!',
    dosage: '약량 기준 1~2ml/m² 농도로 15일 간격 월 2회 처리',
    recommendation: '13종 L-아미노산으로 뿌리 활력과 잔디 밀도를 높여줍니다.',
    mechanism: '주요 성분: 해조 추출물(30% 이상), 아미노산(30% 이상), 펄빅산(15%), 단백질, 수용성 철(3%), 유황(3%) 등을 함유하고 있습니다.\\n천연 성장호르몬: 사이토카닌, 옥신, 지베렐린 등 천연 성장호르몬의 작용으로 세포 분열과 생육을 강력하게 촉진합니다.\\n복합 영양 공급: 비타민 복합체, 킬레이트 미량요소, 셀레늄, 요오드 등 희귀 요소를 포함한 100% 영양 성분이 이온화되어 즉각적으로 이용됩니다.\\n면역 및 대사 활성: 품부한 L-아미노산과 단백질 성분이 효소 활성을 돕고 면역을 담당하는 항체 형성에 기여합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20root%20development%2C%20left%20side%20weak%20shallow%20roots%2C%20right%20side%20strong%20deep%20root%20system%2C%20split%20screen%2C%20plant%20root%20photography&width=800&height=400&seq=beforeafter6&orientation=landscape',
    usageDetail: [
      '권장 사용량: ㎡당 1~2ml의 약량을 사용합니다.',
      '희석 물량: 살포 장비에 따라 ㎡당 100~200ml의 물에 희석하여 사용하십시오.',
      '살포 주기: 15일 간격으로 월 2회 처리를 권장합니다.',
      '살포 팁: 엽면 시비 시 추가 관수는 필요하지 않으며, 대부분의 농약 및 비료와 혼용이 가능합니다.'
    ],
    caution: [
      '발효 대사산물로 인한 침전물이 있을 수 있으니 사용 전 반드시 충분히 흔들어 주십시오.',
      '잔디의 상태나 기후 조건에 따라 희석 농도 및 살포 주기를 적절히 조절하십시오.',
      '직사광선을 피하고 건조하고 서늘한 암소에 보관하십시오.',
      '절대 음용하지 마십시오.'
    ]
  },
  {
    id: 'npk',
    name: '엔.피.케이 (21-17-17)',
    nameEn: 'N.P.K (21-17-17)',
    subtitle: '',
    volume: '10L',
    category: 'growth',
    description: '전통 누룩 발효 공법을 통해 화학비료의 단점을 보완하고 흡수율을 100%로 끌어올린 고성능 발효 액비입니다. 유효 미생물과 펄빅산을 함유하여 토양 내 미생물 생태계를 활성화하고 양이온 치환 용량(CEC)을 개선하여 잔디의 건전한 생육을 지원합니다.',
    thumbnail: 'https://i.imgur.com/oyPhQNC.jpeg',
    image: 'https://readdy.ai/api/search-image?query=blue%20and%20white%20NPK%20fertilizer%20bottle%20with%2021-17-17%20formula%20on%20clean%20white%20background%2C%20professional%20agricultural%20product%20photo%2C%20modern%20design%2C%20premium%20quality%2C%20studio%20lighting&width=400&height=500&seq=prod4&orientation=portrait',
    features: [
      '전통 누룩 발효를 거친 4종 복합비료로, 잔디 및 작물이 성분을 100% 흡수할 수 있도록 제조되었습니다.',
      '성분이 완전히 흡수되어 토양 내 염류 집적 현상을 방지합니다.',
      '발효 대사산물인 효소와 방선균이 포함되어 있어 식물 활력 증진에 도움을 줍니다.',
      '유효 미생물이 토양 내 뿌리 근처(근권)의 토종 미생물을 활성화합니다.',
      '펄빅산 성분이 함유되어 토양의 양이온 치환 용량(CEC)에 긍정적인 영향을 미칩니다.'
    ],
    usage: '비료를 줘도 엽류가 빨갛게 흡수가 안 되어 고민하는 분께 추천!',
    dosage: '약량 기준 1~2ml/m² (물량 100~200ml/m²)',
    recommendation: '전통 발효 공법으로 비료 흡수율을 극대화한 복합 비료입니다.',
    mechanism: '주요 성분: 질소·인산·가리(21-17-17 배합 25%), 펄빅산(5%), 킬레이트 철(1%), 방선균 및 유효 미생물, 수용성 붕소(0.05%), 수용성 몰리브덴(0.0005%).\\n고효율 흡수: 발효 공법을 통해 이온화된 영양소가 식물 체내로 빠르게 스며들어 즉각적인 효과를 발현합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20plant%20nutrition%2C%20left%20side%20pale%20yellow%20leaves%2C%20right%20side%20vibrant%20green%20healthy%20leaves%2C%20split%20screen%2C%20agricultural%20photography&width=800&height=400&seq=beforeafter7&orientation=landscape',
    usageDetail: [
      '약량 기준 1-2ml/m²',
      '물량 100-200ml/m²',
      '생육 전반에 걸쳐 사용'
    ],
    caution: [
      '강산성제, 강알kal리제, 석회유황합제, 기계유제와의 혼용은 피해주십시오.',
      '절대 음용하지 마십시오.'
    ]
  },
  {
    id: 'gold-kali',
    name: '골드-칼리 (K 36%)',
    nameEn: 'Gold Kali',
    subtitle: '',
    volume: '1L',
    category: 'growth',
    description: '비해 걱정 없이 신속하게 칼륨을 보충할 수 있는 고농축 액상 칼륨 비료입니다. 하절기 각종 환경 스트레스에 대한 내성을 높여주며, 광합성 능력 향상과 세포 내 삼투압 조절을 통해 잔디의 탄력성을 유지하는 데 탁월한 효과를 보입니다.',
    thumbnail: 'https://readdy.ai/api/search-image?query=white%20plastic%20fertilizer%20bottle%20container%20centered%20on%20pure%20white%20background%2C%20professional%20product%20photography%2C%20clean%20minimalist%20style%2C%20soft%20studio%20lighting%2C%20no%20shadows%2C%20no%20text%20no%20label&width=400&height=400&seq=pop4v2&orientation=squarish',
    image: 'https://readdy.ai/api/search-image?query=golden%20yellow%20potassium%20fertilizer%20bottle%20on%20clean%20white%20background%20with%20fruit%20elements%2C%20professional%20product%20photography%2C%20premium%20agricultural%20solution%2C%20minimalist%20style%2C%20warm%20lighting&width=400&height=500&seq=prod5&orientation=portrait',
    features: [
      '엽면살포가 가능한 액상 가리 제품으로 칼륨 함량이 매우 높으면서도 잔디에 비해(비료해)가 없습니다.',
      '하절기 고온 스트레스 및 각종 병충해에 대한 잔디의 내성을 강화합니다.',
      '잔디의 광합성 능력을 향상시켜 영양 축적을 돕습니다.',
      '구연산 기반의 칼륨(Citric acid based Potassium)으로 제조되어 흡수가 빠릅니다.'
    ],
    usage: '고온기 스트레스로 작물이 축 늘어져 고민하는 분께 추천!',
    dosage: '3~4주 간격으로 엽면시비 (0.5~1ml/m²) 또는 토양시비',
    recommendation: '고온기 작물 탄력 유지와 스트레스 극복으로 고민하시는 분께 추천합니다.',
    mechanism: '주요 성분: 칼륨(K2O 36%), 해조류(2%), 붕소(0.05%), 몰리브덴(0.0005%).\\n생리 조절 기능: 세포 내 산도(pH) 및 삼투압을 조절하여 수분 밸런스를 유지하고 잔디의 탄력성을 지켜줍니다.\\n결핍 해소: 하절기 잔디 잎 테스트 시 부족하기 쉬운 칼륨 성분을 신속하게 공급합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20plant%20stress%20recovery%2C%20left%20side%20wilted%20drooping%20plants%2C%20right%20side%20firm%20upright%20healthy%20plants%2C%20split%20screen%2C%20agricultural%20photography&width=800&height=400&seq=beforeafter8&orientation=landscape',
    usageDetail: [
      '엽면 시비: ㎡당 0.5~1ml의 약량을 사용합니다.',
      '토양 시비: ㎡당 0.8~1.2ml의 약량을 사용합니다.',
      '시비 간격: 3~4주 간격으로 시비하며, 농약과 혼용이 가능합니다.',
      '희석 물량: ㎡당 100~200ml의 물량에 맞춰 사용하십시오.'
    ],
    caution: [
      '제품 용량은 1L 포장 단위로 구성되어 있습니다.',
      '권장 사용법을 준수하여 시비하십시오.'
    ]
  },
  {
    id: 'gs101',
    name: 'GS101',
    nameEn: 'Gs101',
    subtitle: '',
    volume: '1L',
    category: 'growth',
    description: '전통 발효 공법으로 63%의 고농도 동물성 아미노산을 완전 이온화하여 흡수율을 극대화한 식물 활력 촉진제입니다. 화학적 N.P.K를 첨가하지 않아 농도 장애 우려가 없으며, 병해나 약해 등 각종 스트레스로 손상된 잔디의 빠른 회복과 뿌리 발육을 돕습니다.',
    thumbnail: 'https://i.imgur.com/Utg2BnA.jpeg',
    image: 'https://readdy.ai/api/search-image?query=high%20concentration%20amino%20acid%20GS101%20fertilizer%20bottle%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20premium%20agricultural%20solution%2C%20minimalist%20style%2C%20soft%20lighting&width=400&height=500&seq=prod15&orientation=portrait',
    features: [
      '100% 완전 이온화되어 버려지는 성분 없이 탁월한 효능을 발휘합니다.',
      '화학적 N.P.K 무첨가(아민태 질소만 포함)로 농도 장애와 비해 걱정 없이 안정적으로 사용 가능합니다.',
      '우박, 해충, 가뭄, 고온 및 약해 등으로 손상된 지역의 회복력이 매우 뛰어납니다.',
      '예지(Mowing) 스트레스 회복, 녹색 기간 연장, 물 이용 효율 향상 효과가 있습니다.',
      '다량의 유기물 공급을 통해 토양 구조를 개선합니다.'
    ],
    usage: '약해, 기형, 병해 등 갑작스러운 손상으로 빠른 회복이 필요한 분께 추천!',
    dosage: '그린 기준 0.5~1.0ml/m², 7~10일 간격 1~3회 처리',
    recommendation: '약해나 병해로 손상된 작물의 빠른 회복으로 고민하시는 분께 추천합니다.',
    mechanism: '주요 성분: 고농도 동물성 아미노산(63%), 글루코스(10%), 붕소(0.05%), 몰리브덴(0.0005%).\\n유리아미노산 효과: 분자량이 작은 아미노산 비율이 높아 활성이 빠릅니다.\\n주요 아미노산 기작:\\n· 프롤린: 냉해 및 고온 스트레스 경감, 노화 억제.\\n· 글리신·알라닌: 생육 촉진, 엽색 유지 기간 연장, 타 양분의 이동 및 흡수 촉진.\\n· 리이신: 작물 보호제와 혼용 시 성능 향상 기여.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20plant%20damage%20recovery%2C%20left%20side%20damaged%20stressed%20plants%2C%20right%20side%20recovered%20healthy%20plants%2C%20split%20screen%2C%20agricultural%20photography&width=800&height=400&seq=beforeafter9&orientation=landscape',
    usageDetail: [
      '경엽 처리: 그린(0.5~1.0ml/㎡), 티·페어웨이(0.3~0.5ml/㎡).',
      '관주 처리: 그린·페어웨이 공통 1.0~1.5ml/㎡.',
      '처리 주기: 7~10일 간격으로 1~3회 연속 처리 시 효과적입니다.'
    ],
    caution: [
      '육묘기부터 생육 전 단계에 걸쳐 사용이 가능합니다.',
      '1L 포장 단위 제품입니다.'
    ]
  },
  {
    id: 'k-worm',
    name: 'K웜',
    nameEn: 'K-Worm',
    subtitle: '',
    volume: '1L',
    volumeOptions: ['1L', '5L', '10L'],
    category: 'growth',
    description: '국내 최초 특허 기술로 지렁이 분변토에서 유익 미생물을 추출하여 제조한 친환경 생육 활성제입니다. 토양 내 유익균을 폭발적으로 증식시켜 뿌리 길이를 증가시키고 대취 분해를 촉진하며, 답압이나 드라이스팟 등 각종 스트레스에 대한 잔디의 저항력을 높여줍니다.',
    thumbnail: 'https://i.imgur.com/4OzIatL.jpeg',
    image: 'https://readdy.ai/api/search-image?query=K-WORM%20earthworm%20castings%20liquid%20fertilizer%20bottle%20brown%20on%20clean%20white%20background%2C%20professional%20agricultural%20product%20photo%2C%20organic%20style%2C%20premium%20quality%2C%20natural%20lighting&width=400&height=500&seq=prod16&orientation=portrait',
    features: [
      '특허받은 기술로 지렁이 분변토 내 유익 미생물(바실러스 등)의 생육 환경을 최적화합니다.',
      '잔디의 밀도를 높이고 뿌리 길이를 직접적으로 증가시켜 볼 마크 감소와 회복력 향상에 기여합니다.',
      '토양 내 불용화된 양분을 식물이 흡수 가능한 형태로 분해하여 지속적인 영양을 공급합니다.',
      '인위적인 웃자람 없이 건강한 생육을 유도하며 대취 제거 효과가 있습니다.',
      '건조에 견디는 힘을 길러 드라이스팟(LDS) 예방에 도움을 줍니다.',
      '병원성 곰팡이균을 억제하여 잔디의 내병성을 향상시킵니다.'
    ],
    usage: '뿌리 깊이를 올리고 토양의 완충력을 높이고 싶은 분께 추천!',
    dosage: '일반 관리 2ml/m², 집중 관리 4ml/m² (2주 간격)',
    recommendation: '특허받은 지렁이 분변토 추출 기술로 토양 생태계를 활성화합니다.',
    mechanism: '주요 성분: 천연 N.P.K(0.1-0.1-0.1), 지렁이 분변토 추출물, 바실러스 미생물, 수용성 붕소(0.05%), 수용성 몰리브덴(0.0005%).\\n미생물 활성: 지렁이 분변토에서 추출한 유익 미생물이 토양 내에서 증식하며 뿌리 발육을 촉진합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20soil%20microbial%20activity%2C%20left%20side%20poor%20soil%2C%20right%20side%20rich%20dark%20soil%20with%20earthworms%2C%20split%20screen%2C%20agricultural%20photography&width=800&height=400&seq=beforeafter10&orientation=landscape',
    usageDetail: [
      '일반 관리: ㎡당 2ml의 약량을 2주 간격으로 사용하십시오.',
      '대취 집중 관리: ㎡당 4ml의 약량을 2주 간격으로 시비 시 최상의 효과를 볼 수 있습니다.',
      '혼용 안내: 모든 살균제와 혼용살포가 가능합니다.'
    ],
    caution: [
      '포장 용량은 1L, 5L, 10L 단위로 구성되어 있습니다.',
      '제품 개봉 후에는 미생물 활성을 위해 가급적 빠르게 소진하십시오.',
      '절대 음용하지 마십시오.'
    ]
  },
  {
    id: 'land-rich',
    name: '랜드리치',
    nameEn: 'Land-Rich',
    subtitle: '',
    volume: '1L',
    category: 'growth',
    description: '저온기와 고온기 스트레스를 동시에 관리할 수 있도록 설계된 4종 복합 스트레스 회복 액비입니다. 질산태와 요소태 질소를 적절히 배합하여 빠른 흡수와 지속적인 효과를 모두 갖췄으며, 품부한 유기물과 미생물 먹이를 통해 토양 생태계를 강화하고 잔디의 밀도를 유지합니다.',
    thumbnail: 'https://i.imgur.com/bybqAS9.jpeg',
    image: 'https://readdy.ai/api/search-image?query=Land-Rich%20humic%20fulvic%20acid%20fertilizer%20bottle%20dark%20brown%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20premium%20agricultural%20solution%2C%20minimalist%20style%2C%20warm%20lighting&width=400&height=500&seq=prod17&orientation=portrait',
    features: [
      '미생물 먹이가 함유되어 뿌리 근처의 유효 미생물을 증식시키고 토양 활력을 높입니다.',
      '저온기에 신초 발생을 돕고 엽색을 진하게 유지하여 잔디 밀도를 확보합니다.',
      '구연산 성분이 함유되어 고온기 하고 현상(Summer decline)을 늦추거나 완화하는 데 도움을 줍니다.',
      '발효 대사산물인 효소와 방선균을 함유하여 충해 및 균해 예방 효과를 겸비했습니다.',
      '질소 성분이 빠르게 흡수되는 형태와 지속적으로 공급되는 형태로 구성되어 건전한 생육을 유지합니다.'
    ],
    usage: '저온기에 잔디 밀도가 떨어지고 엽색이 연해져 고민하는 분께 추천!',
    dosage: '약량 1~2ml/m² 기준으로 엽면 및 관주 처리 가능',
    recommendation: '풀빅산과 휴믹산의 시너지로 저온기 생육을 촉진합니다.',
    mechanism: '주요 성분: 질소·인산·가리(18-3-6), 펄빅산(10%), 유기물(30%), 발효 유황(2%), 방선균, 붕소, 몰리브덴.\\n미량요소 기작: 붕소에 의한 신초 발생 촉진, 철에 의한 엽록소 증가, 아연에 의한 뿌리 발생 유도 기능이 포함되어 있습니다.\\n에너지 공급: 당과 구연산을 함유하여 저온기 등 환경이 열악할 때 영양 흡수 효율을 높여줍니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20winter%20grass%20recovery%2C%20left%20side%20pale%20thin%20grass%2C%20right%20side%20dense%20green%20grass%2C%20split%20screen%2C%20turf%20photography&width=800&height=400&seq=beforeafter11&orientation=landscape',
    usageDetail: [
      '약량 1-2ml/m² 기준',
      '희석 물량: ㎡당 100~200ml의 물량에 맞춰 사용하십시오.',
      '처리 방식: 엽면살포와 관주 처리 모두 가능하여 상황에 맞게 적용할 수 있습니다.'
    ],
    caution: [
      '강알kal리성 및 강산성 제제, 유황합제, 기계유제, 석회보르도액 등과의 혼용을 금지합니다.',
      '10L 포장 단위 제품입니다.',
      '피부 접촉 시 흐르는 물에 깨끗이 세척하고 절대 음용하지 마십시오.'
    ]
  },

  // ===== 카테고리 3: 환경 스트레스 및 기능성 =====
  {
    id: 'glucomin',
    name: '글루코민',
    nameEn: 'Glucomin',
    subtitle: '',
    volume: '1L',
    category: 'stress',
    description: '천연 희토류 미네랄과 70여 종의 미네랄을 이상적으로 배합하여 잔디의 이상기후 저항력을 극대화하는 생리활성 촉진제입니다. 고·저온 및 냉해 등 각종 스트레스를 효과적으로 해소하며, 뿌리 생육 활성화와 빠른 수세 회복을 돕는 100% 친환경 자재입니다.',
    thumbnail: 'https://i.imgur.com/bybqAS9.jpeg',
    image: 'https://readdy.ai/api/search-image?query=clear%20transparent%20bottle%20with%20blue%20liquid%20glucomin%20formula%20on%20clean%20white%20background%2C%20professional%20agricultural%20product%20photo%2C%20modern%20minimalist%20design%2C%20premium%20quality%2C%20soft%20lighting&width=400&height=500&seq=prod6&orientation=portrait',
    features: [
      '천연 광물에서 추출한 희토류를 함유하여 식물체 내 단백질 함량을 높이고 이상기후에 대한 저항력을 증가시킵니다.',
      '수분 이동 및 세포 팽압 유지를 통해 냉해, 건조해 및 여름철 하고 현상을 예방합니다.',
      '생장점 세포분열을 촉진하여 지하부 뿌리 생육을 강력하게 활성화합니다.',
      '질소 이용률을 향상시키고 질산염 및 농약 잔류량을 감소시키는 효과가 있습니다.',
      '화학적 N, P, K를 함유하지 않아 고온기에도 비해(비료해) 걱정 없이 안전하게 사용 가능합니다.',
      '약해나 비해 발생 시 사용하면 잔디의 회복 속도를 비약적으로 높여줍니다.'
    ],
    usage: '냉해, 약해 등으로 작물 컨디션이 급격히 나빠져 고민하는 분께 추천!',
    dosage: '약량 1ml/m² 기준으로 2주 간격 살포',
    recommendation: '희토류 성분이 냉해와 약해로 고민하시는 분께 추천합니다.',
    mechanism: '주요 성분: 희토류(란탄계 17개 원소), 70여 종의 미네랄, 고농도 글루코스(50% 이상), L-α아미노산, 펄빅산, 10여 종의 비타민 함유.\\n희토류 메커니즘: 식물 생장에 맞게 배합된 희토류가 효소 활성을 촉진하여 작물의 생리적 기능을 극대화합니다.\\n활력 증진: 기능성 해조 추출물과 비타민, 아미노산이 복합 작용하여 잔디의 생리 활성에 큰 효과를 발휘합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20climate%20stress%20recovery%2C%20left%20side%20frost%20damaged%20plants%2C%20right%20side%20healthy%20recovered%20plants%2C%20split%20screen%2C%20agricultural%20photography&width=800&height=400&seq=beforeafter12&orientation=landscape',
    usageDetail: [
      '권장 사용량: ㎡당 1ml의 약량을 사용합니다.',
      '희석 물량: ㎡당 100~200ml의 물에 희석하여 사용합니다.',
      '살포 주기: 2주 간격으로 정기적인 살포를 권장합니다.'
    ],
    caution: [
      '절대 음용하지 마십시오.',
      '강산성 및 강알kal리성 제료와 혼용 시에는 반드시 소량 실험 후 사용하십시오.',
      '제품 용량은 1L 단위로 구성되어 있습니다.'
    ]
  },
  {
    id: 'frost-reduce',
    name: '프로스트레듀스',
    nameEn: 'Frost Reduce',
    subtitle: '',
    volume: '1L',
    category: 'stress',
    description: '이산화티탄과 친환경 식물성 오일을 배합하여 서리 피해 및 동해를 예방하는 특수 경감제입니다. 자외선과 적외선을 열에너지로 전환하여 동해 방지하며, 서리 내린 잔디 위 발자국이나 장비 바퀴 자국으로 인한 손상을 획기적으로 줄여줍니다.',
    thumbnail: 'https://i.imgur.com/3SJ36vq.jpeg',
    image: 'https://readdy.ai/api/search-image?query=white%20and%20ice%20blue%20frost%20protection%20fertilizer%20bottle%20on%20clean%20white%20background%20with%20snowflake%20elements%2C%20professional%20product%20photography%2C%20premium%20agricultural%20solution%2C%20minimalist%20style%2C%20cool%20lighting&width=400&height=500&seq=prod7&orientation=portrait',
    features: [
      '자외선과 적외선을 흡수해 열에너지로 전환·방출함으로써 겨울철 동해 방지에 탁월한 효능을 보입니다.',
      '기존 파라핀 오일 코팅 방식이 아닌, 잔디가 흡수하여 조직력을 강화하는 방식으로 발자국 피해를 예방합니다.',
      '이슬이 내리기 전 살포 시 잔디에 이슬이 맺히지 않도록 돕습니다.',
      '서리가 빨리 녹도록 유도하여 발자국 피해 발생 시 회복 속도를 빠르게 합니다.',
      '식물성 오일 성분이 수분을 유지하여 겨울철 건조 피해 예방에도 효과적입니다.',
      '예초를 하지 않는 조건에서 약 3~7일간 효과가 지속됩니다.'
    ],
    usage: '겨울철 서리 자국이나 건조 피해 문제로 고민하는 분께 추천!',
    dosage: '100배 희석살포 (엽서리 예시 50배)',
    recommendation: '식물성 특수 오일로 겨울철 서리와 동해를 효과적으로 차단합니다.',
    mechanism: '주요 성분: 이산화티탄(다량), 식물성 오일(다량), 해조 알긴산(5%), 수용성 붕소(0.05%), 수용성 몰리브덴(0.0005%).\\n열에너지 전환: 함유된 특수 물질이 광 에너지를 열로 바꾸어 잔디 온도를 유지시켜 동해를 방지합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20frost%20damage%20prevention%2C%20left%20side%20frost%20damaged%20brown%20leaves%2C%20right%20side%20protected%20green%20leaves%2C%20split%20screen%2C%20winter%20agricultural%20photography&width=800&height=400&seq=beforeafter13&orientation=landscape',
    usageDetail: [
      '일반 살포: 100배 희석하여 ㎡당 50~100ml의 물량으로 살포합니다.',
      '된서리 예상 시: 50배로 농도를 높여 희석살포하는 것을 권장합니다.',
      '장비 활용: 멀티프로 안개 분무(08노즐 이하) 사용 시 물 500L에 본 제품 5병(5L)을 혼합하여 5,000㎡에 살포하십시오.'
    ],
    caution: [
      '살포 후 절대 관수 금지 (성분 흡수를 위함).',
      '타 제품과 혼용이 가능하나 단용 사용 시 최고의 효과를 얻을 수 있습니다.',
      '사용 전 충분히 흔들어 주시고 절대 음용하지 마십시오.',
      '제품 용량은 1L 단위입니다.'
    ]
  },
  {
    id: 'aqua-screen',
    name: '아쿠아탑스크린',
    nameEn: 'Aqua-Top Screen',
    subtitle: '',
    volume: '1L',
    category: 'stress',
    description: '유해 자외선을 선택적으로 차단하고 이로운 가시광선 흡수를 도와 잔디의 광합성 효율을 극대화하는 자외선 보호제입니다. 여름철 지표면 온도를 낮추어 품질을 높이고, 겨울철에는 지온 증가를 도와 색상 유지 기간을 늘려주는 고농축 착색 겸용 자재입니다.',
    thumbnail: 'https://i.imgur.com/DvR55yt.jpeg',
    image: 'https://readdy.ai/api/search-image?query=white%20bottle%20with%20UV%20protection%20formula%20on%20clean%20white%20background%20with%20sun%20shield%20elements%2C%20professional%20product%20photography%2C%20premium%20agricultural%20solution%2C%20minimalist%20style%2C%20bright%20lighting&width=400&height=500&seq=prod8&orientation=portrait',
    features: [
      '잔디에 해로운 UVA, UVB는 차단하고, 광합성에 유용한 가시광선은 투과시켜 광 효율을 높여줍니다.',
      '여름철 잔디 표면 온도를 저하시켜 고온기 품질 저하를 예방합니다.',
      '겨울철 휴면 잔디의 착색 효과가 오래 지속되며 지온 증가를 도와 생육에 도움을 줍니다.',
      '단순 코팅을 넘어 기공으로 성분이 스며들어 더욱 자연스럽고 우수한 색상을 발현합니다.',
      '과도한 광 스트레스로부터 세포 손상을 방지하고 잔디의 생육 건康을 향상시킵니다.',
      '장기간 보관 시에도 제품이 굳지 않아 사용이 편리합니다.'
    ],
    usage: '강한 햇빛에 작물이 타거나 광 스트레스로 고민하는 분께 추천!',
    dosage: '6~9월 사이 약 3주 간격 사용 권장',
    recommendation: '유해 UV는 차단하고 광합성에 필요한 빛만 투과시킵니다.',
    mechanism: '주요 성분: 고농축 안료(70%), Titanium Dioxide(30.9%), Zinc Oxide(10%), 수용성 붕소(0.05%), 수용성 몰리브덴(0.0005%).\\n광합성 최적화: 식물이 흡수하는 430~620nm 범위의 가시광선을 효과적으로 사용할 수 있도록 돕습니다.\\n온도 저감: 티타늄 및 아연 성분이 자외선을 차단 및 반사하여 잔디의 온도 상승을 억제합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20UV%20protection%20on%20plants%2C%20left%20side%20sun%20burned%20leaves%2C%20right%20side%20healthy%20protected%20leaves%2C%20split%20screen%2C%20summer%20agricultural%20photography&width=800&height=400&seq=beforeafter14&orientation=landscape',
    usageDetail: [
      'UV 차단(여름철): 10,000㎡당 0.75~1.5L의 약량을 400~800L의 물에 희석하여 사용합니다.',
      '권장 시기: 6월 중순부터 9월 말까지 약 3주 간격으로 사용을 권장합니다.'
    ],
    caution: [
      '미량요소 복합비료 등록 제품이며 절대 음용을 금지합니다.',
      '작업 시 안료가 의복이나 신발에 묻지 않도록 주의하고 고무장갑을 착용하십시오.',
      '제품 용량은 1L 단위입니다.'
    ]
  },
  {
    id: 'aqua-color',
    name: '아쿠아컬러',
    nameEn: 'Aqua-Color',
    subtitle: '',
    volume: '1L',
    volumeOptions: ['1L', '5L', '10L'],
    category: 'stress',
    description: '사계절 내내 천연 녹색을 유지해 주는 친환경 수성 착색제이자 미량요소 복합비료입니다. 약해나 비해로 잔디 상태가 좋지 않을 때 응급 처방용으로 효과적이며, 빠른 건조 속도와 강력한 코팅 효과로 겨울철 동해 방지에도 탁월합니다.',
    thumbnail: 'https://i.imgur.com/wiyzhSh.jpeg',
    image: 'https://readdy.ai/api/search-image?query=vibrant%20red%20and%20purple%20color%20enhancer%20fertilizer%20bottle%20on%20clean%20white%20background%20with%20colorful%20fruit%20elements%2C%20professional%20product%20photography%2C%20premium%20agricultural%20solution%2C%20minimalist%20style%2C%20vivid%20lighting&width=400&height=500&seq=prod9&orientation=portrait',
    features: [
      '고농축 수성 안료(100%)를 사용하여 선명하고 오래 지속되는 녹색을 발현합니다.',
      '약해, 비해 등으로 변색된 잔디의 시각적 회복을 돕는 응급 처방용으로 용이합니다.',
      '코팅 효과가 뛰어나 가을 및 겨울철 건조 피해와 동해 방지에 도움을 줍니다.',
      '건조 속도가 매우 빨라 살포 후 플레이에 지장을 주지 않으며 경제적입니다.',
      '킬레이트 철(Fe)과 해조류를 함유하여 더욱 깊고 은은한 녹색을 구현합니다.',
      '절개지의 표면 도포 시에도 우수한 착색 효과를 보입니다.'
    ],
    usage: '동절기 변색이나 빠른 시각적 품질 개선으로 고민하는 분께 추천!',
    dosage: '50~100배 희석하여 잎이 충분히 젖을 정도 살포',
    recommendation: '킬레이트 철 성분으로 즉각적인 착색과 동해 방지로 고민하시는 분께 추천합니다.',
    mechanism: '주요 성분: 고농축 수성 안료, 수용성 붕소, 수용성 몰리브덴, 킬레이트 철, 해조류 추출물.\\n흡수 및 코팅: 잔디 기공을 통해 성분이 흡수됨과 동시에 표면을 코팅하여 시각적 효과와 보호 기능을 동시에 수행합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20plant%20coloring%2C%20left%20side%20pale%20faded%20leaves%2C%20right%20side%20vibrant%20green%20colored%20leaves%2C%20split%20screen%2C%20agricultural%20photography&width=800&height=400&seq=beforeafter15&orientation=landscape',
    usageDetail: [
      '희석 배수: 물과 1:50 ~ 1:100 비율로 희석하여 사용합니다.',
      '약량 기준: ㎡당 10~20cc의 희석액을 잔디 잎이 충분히 젖을 정도로 엽면살포합니다.',
      '색상 조절: 투입하는 물의 양을 조절하여 원하는 색상의 농도를 맞출 수 있습니다.'
    ],
    caution: [
      '미량요소 복합비료로 등록된 제품입니다.',
      '사용 전 안료가 골고루 섞이도록 충분히 흔들어 주십시오.',
      '1L, 5L, 10L 다양한 용량으로 구성되어 있습니다.'
    ]
  },
  {
    id: 'green-keeper',
    name: '그린키퍼',
    nameEn: 'Green-Keeper',
    subtitle: '',
    volume: '5L',
    category: 'stress',
    description: '공인 시험 기관에서 생산된 무독성 친환경 수분 침투제로 토양 속 침투력과 보유력을 향상시켜 물빠짐을 빠르게 합니다.',
    thumbnail: 'https://i.imgur.com/tstwqNs.jpeg',
    image: 'https://readdy.ai/api/search-image?query=green%20keeper%20moisture%20penetrant%20fertilizer%20bottle%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20premium%20agricultural%20solution%2C%20minimalist%20style%2C%20fresh%20green%20lighting&width=400&height=500&seq=prod20&orientation=portrait',
    features: [
      '수분 침투 및 보습 관리', '토양 속 침투력과 보유력을 향상시켜 물빠짐을 빠르게 함'
    ],
    usage: '드라이스팟(LDS)이나 건조 피해로 고민하는 분께 추천!',
    dosage: '물 1000ml에 약 5L 희석하여 2주 간격 살포',
    recommendation: '무독성 친환경 수분 침투제로 드라이스팟 문제로 고민하시는 분께 추천합니다.',
    mechanism: '수분 침투 및 보습 관리에 효과적입니다. 공인 시험 기관에서 생산된 무독성 친환경 수분 침투제로, 토양 속 침투력과 보유력을 향상시켜 물빠짐을 빠르게 하고 수분 스트레스를 해소합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20soil%20moisture%20penetration%2C%20left%20side%20dry%20cracked%20soil%2C%20right%20side%20moist%20healthy%20soil%2C%20split%20screen%2C%20agricultural%20photography&width=800&height=400&seq=beforeafter16&orientation=landscape',
    usageDetail: [
      '물 1000ml에 약 5L 희석',
      '2주 간격 살포',
      '건조 피해 발생 시 집중 관리'
    ],
    caution: [
      '과습 토양에는 사용 불필요',
      '살포 후 충분한 관수 필요',
      '동결 시 품질 저하 주의'
    ]
  },
  {
    id: 'aqualor',
    name: '아쿠아로',
    nameEn: 'Aqualor',
    subtitle: '',
    volume: '1L',
    category: 'stress',
    description: '뜨거운 여름철 유해 자외선(UVA, UVB)을 효과적으로 차단하여 잔디의 표면 온도를 낮추고 품질을 향상시키는 자외선 차단 및 착색제입니다. 수분 증산을 억제하여 물 사용량을 줄여주며, 침전이나 굳는 현상 없이 안정적인 착색 효과를 제공합니다.',
    thumbnail: 'https://i.imgur.com/9LnukjR.jpeg',
    image: 'https://readdy.ai/api/search-image?query=aqualor%20titanium%20ion%20fertilizer%20bottle%20silver%20blue%20on%20clean%20white%20background%2C%20professional%20agricultural%20product%20photo%2C%20modern%20design%2C%20premium%20quality%2C%20cool%20lighting&width=400&height=500&seq=prod21&orientation=portrait',
    features: [
      '하절기 강한 자외선(UVA, UVB)을 차단하여 광 스트레스로부터 잔디를 보호합니다.',
      '물 사용량을 감소시켜 잔디 표면 온도를 낮추고 고온기 건康을 유지합니다.',
      '잔디의 밀도를 높이고 광합성 및 호흡량을 증가시키는 데 도움을 줍니다.',
      '자연스러운 착색 효과를 제공하여 시각적인 잔디 품질을 높여줍니다.',
      '타사 제품과 달리 장기간 보관 시에도 침전되거나 딱딱하게 굳는 현상이 없습니다.'
    ],
    usage: '뜨거운 여름철 햇빛에 작물 품질이 열화 되어 고민하는 분께 추천!',
    dosage: '약량기준 0.5~1ml/m² (7일 간격 사용)',
    recommendation: '티타늄이온 복합체가 여름철 강한 자외선으로부터 작물을 보호합니다.',
    mechanism: 'UV 차단 및 착색 효과에 뛰어납니다. 주요 성분: Titanium Dioxide 복합체(30.12%), 산화아연(Zinc Oxide 10.88%), 수용성 붕소(0.05%), 수용성 몰리브덴(0.0005%).\\nUV 차단 기작: 산화아연과 티타늄 복합체가 잔디 표면에서 자외선을 반사 및 차단하여 세포 손상을 방지합니다.',
    beforeAfterImage: 'https://readdy.ai/api/search-image?query=before%20and%20after%20comparison%20of%20summer%20heat%20protection%2C%20left%20side%20heat%20stressed%20wilted%20plants%2C%20right%20side%20cool%20protected%20healthy%20plants%2C%20split%20screen%2C%20summer%20agricultural%20photography&width=800&height=400&seq=beforeafter17&orientation=landscape',
    usageDetail: [
      '권장 사용량: ㎡당 0.5~1ml의 약량을 사용합니다.',
      '희석 물량: ㎡당 100~200ml의 물에 희석하여 사용하십시오.',
      '살포 주기: 여름철 7일 간격으로 정기 사용 시 최상의 컨디션을 유지합니다.'
    ],
    caution: [
      '제품 용량은 1L 단위 포장입니다.',
      '사용 전 내용물이 잘 섞이도록 충분히 흔들어 주십시오.'
    ]
  }
];

// ===== 유틸리티 함수 =====

// 제품 ID로 제품 찾기
export const getProductById = (id: string): Product | undefined => {
  return products.find(p => p.id === id);
};

// 카테고리별 제품 가져오기
export const getProductsByCategory = (category: CategoryType): Product[] => {
  return products.filter(p => p.category === category);
};

// 여러 제품 ID로 제품 목록 가져오기 (메인페이지 진열용)
export const getProductsByIds = (ids: string[]): Product[] => {
  return ids.map(id => getProductById(id)).filter((p): p is Product => p !== undefined);
};

// 카테고리 순서
export const categoryOrder: CategoryType[] = ['disease', 'growth', 'stress'];